/**
 * Non-colour scales. Values are unitless-in-name / rem-in-value so Tailwind and
 * NativeWind read them identically; NativeWind converts rem to px at build time.
 */

/** 4px base grid. `p-4` is 1rem on both platforms. */
export const spacing = {
  0: '0rem',
  px: '1px',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  2.5: '0.625rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
} as const

export const radii = {
  none: '0rem',
  sm: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  full: '9999px',
} as const

export const fontSize = {
  /** Small print under a title: a drawer's facts (whose, when). */
  '2xs': '0.6875rem',
  xs: '0.75rem',
  /** A pill's label (a plan's sections): between xs and sm. */
  pill: '0.8125rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
} as const

export const fontWeight = {
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const

export const lineHeight = {
  tight: '1.25',
  snug: '1.375',
  normal: '1.5',
  relaxed: '1.625',
} as const

/**
 * Font stacks differ by platform because the system UI font differs. Keeping the
 * *token name* identical is the point — `font-sans` means "the UI font" on both.
 */
export const fontFamily = {
  web: {
    sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  native: {
    sans: 'System',
    mono: 'Menlo',
  },
} as const

/** Minimum touch target for anything a volunteer taps on a phone on a Sunday. */
export const minTouchTarget = 44

/**
 * Component measures: the few lengths every screen repeats, named so one
 * change moves them everywhere (and the staging design editor can tweak
 * them live). Tailwind reads them by name: `px-row-x`, `min-h-row`,
 * `rounded-card`.
 */
export const componentSpacing = {
  /** A list row (listRow): side padding, vertical padding, least height. */
  'row-x': spacing[4],
  'row-y': spacing[3],
  row: spacing[12],
  /** Inputs and selects. */
  'control-x': spacing[3],
  'control-y': spacing[2],
  /** Buttons with words. */
  'button-x': spacing[4],
  'button-y': spacing[2],
  /** An icon-only button's square (40px; a row's own buttons). */
  'icon-button': spacing[10],
  /** Inside a side panel and its title bar. */
  'panel-x': spacing[5],
  'panel-y': spacing[5],
  /** The page's side gutter on a phone. */
  gutter: spacing[4],
} as const

export const componentRadii = {
  /** Cards and list containers. */
  card: radii.xl,
  /** Buttons, inputs, selects, row highlights. */
  control: radii.lg,
  /** Chips and small tags. */
  chip: radii.md,
  /** Sheets from the bottom on a phone. */
  sheet: radii['2xl'],
} as const

/**
 * Motion. Things that respond to a touch take `fast`; things that move into
 * place (panels, rows arriving) take `base` or `slow`. `out` decelerates —
 * quick to start, gentle to land — and is the default; `in-out` is for
 * something that moves across the screen and settles; `spring` overshoots a
 * hair, for confirmations.
 */
export const motion = {
  duration: {
    fast: '120ms',
    base: '200ms',
    slow: '320ms',
  },
  ease: {
    out: 'cubic-bezier(0.32, 0.72, 0, 1)',
    'in-out': 'cubic-bezier(0.65, 0, 0.35, 1)',
    spring: 'cubic-bezier(0.34, 1.4, 0.64, 1)',
  },
} as const
